import { Box, Center, Checkbox, Dialog, Flex, Portal, Spinner, Text } from '@chakra-ui/react';
import { isSessionCategory, SessionCategoryPrefix, type User } from '@halo/chat-sdk';
import { useRefs } from '@halo/hooks';
import { useTranslation } from 'react-i18next';
import {
  Avatar,
  ButtonGray,
  ButtonRed,
  CloseIcon,
  Delete2Icon,
  Download2Icon,
  ErrorIcon,
  Forward2Icon,
  MessageIcon,
  tools,
  Tooltip,
  ZoomInIcon,
  ZoomOutIcon,
} from '@halo/ui-chat-react';
import { downloadImage } from '@halo/utils';
import dayjs from 'dayjs';
import { memo, type PropsWithChildren } from 'react';
import type {
  PluginProps,
  RenderSlideProps,
  Slide,
  SlideImage,
} from 'yet-another-react-lightbox';
import {
  ImageSlide,
  useController,
  useLightboxProps,
  useLightboxState,
} from 'yet-another-react-lightbox';
import './types.d';

const IconButton = memo(function IconButton({
  children,
  onClick,
  reverse,
  alt,
}: PropsWithChildren<{ onClick?: () => void; reverse?: boolean; alt?: string }>) {
  const buttonContent = (
    <Box
      fontSize="lg"
      p="2"
      display="flex"
      alignItems="center"
      justifyContent="center"
      cursor="pointer"
      onClick={onClick}
      transform={reverse === undefined || reverse === false ? 'scaleX(-1)' : undefined}
      overflow="hidden"
    >
      {children}
    </Box>
  );

  if (!alt) return buttonContent;

  return (
    <Tooltip
      showArrow={true}
      content={alt}
      contentProps={{
        style: { zIndex: 9999 },
      }}
    >
      {buttonContent}
    </Tooltip>
  );
});

const DeleteConfirmDialog = forwardRef<
  { open: () => void },
  PropsWithChildren<{
    onConfirm?: () => void;
  }>
>(function DeleteConfirmDialog({ onConfirm, children }, ref) {
  const { t } = useTranslation();
  const [open, { setTrue, setFalse }] = useBoolean(false);

  useImperativeHandle(
    ref,
    () => ({
      open: () => setTrue(),
    }),
    [setTrue]
  );

  const setConfirm = useCallback(() => {
    onConfirm?.();
    setFalse();
  }, [setFalse, onConfirm]);

  return (
    <Dialog.Root
      placement="center"
      open={open}
      onOpenChange={(e) => {
        if (e.open) return;
        setFalse();
      }}
    >
      <Dialog.Backdrop />
      <Portal>
        <Dialog.Positioner zIndex={10000}>
          <Dialog.Content w="428px">
            <Dialog.Header>
              <Flex alignItems="center" gap="2">
                <Box fontSize="6xl" color="fg.error">
                  <ErrorIcon />
                </Box>
                <Text fontSize="md" fontWeight="semibold">
                  {t('chat.deleteMessagesTitle')}
                </Text>
              </Flex>
            </Dialog.Header>
            <Dialog.Body>{children}</Dialog.Body>
            <Dialog.Footer>
              <ButtonGray variant="subtle" color="fg" bg="bg.subtle" onClick={setFalse}>
                {t('buttons.cancel')}
              </ButtonGray>
              <ButtonRed onClick={setConfirm}>{t('buttons.delete')}</ButtonRed>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
});

function Controllers() {
  const { currentSlide } = useLightboxState();
  const { t } = useTranslation();
  const { on, imagePreview: { sessionStore, client, user } = {} } = useLightboxProps();
  const { close } = useController();

  const slide = useMemo(
    () => (currentSlide?.type === 'image' ? currentSlide : null),
    [currentSlide]
  );

  const message = useMemo(() => {
    return slide?.message;
  }, [slide]);

  const file = useMemo(
    () => message?.cli_msg_id && sessionStore?.fileMsgs[message.cli_msg_id]?.file,
    [sessionStore?.fileMsgs, message?.cli_msg_id]
  );

  const fileMsg = useMemo(
    () => ({
      file: file,
      name: message?.payload.name,
    }),
    [message?.payload.name, file]
  );

  const [senderInfo, setSenderInfo] = useState<User | undefined>(undefined);
  const [deleteForEveryone, setDeleteForEveryone] = useState<boolean>(false);

  const isGroupChat = useMemo(() => {
    if (!sessionStore) return false;
    const chatId = sessionStore.session?.chat_id;
    return isSessionCategory(chatId, SessionCategoryPrefix.Group);
  }, [sessionStore]);

  const isGroupOwner = useMemo(() => {
    return sessionStore?.isGroupOwner ?? false;
  }, [sessionStore]);

  const canRestrictDownload = useMemo(() => {
    return sessionStore?.groupAccess?.canRestrictDownload;
  }, [sessionStore]);

  const canRestrictForward = useMemo(() => {
    return sessionStore?.groupAccess?.canRestrictForward;
  }, [sessionStore]);

  const deleteConfirmDialogRef = useRef<{ open: () => void }>(null);

  const isOwnMessage = message?.owner_id === user?.user_id;

  const canDeleteAll = useMemo(() => {
    // if private message, can delete for all
    if (!isGroupChat) return true;
    // other case
    return isOwnMessage || (isGroupChat && isGroupOwner);
  }, [isOwnMessage, isGroupChat, isGroupOwner]);

  useEffect(() => {
    if (!client) return;
    if (!message?.owner_id) return;

    let cancelled = false;
    client.Api.user.resolveUsersInfo(message.owner_id, (user) => {
      if (!cancelled) {
        setSenderInfo(user);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [client, message?.owner_id]);

  useEffect(() => {
    if (!canDeleteAll) return;
    setDeleteForEveryone(true);
  }, [canDeleteAll]);

  const handleZoomIn = useCallback(() => {
    on.imagePreviewZoomIn?.();
  }, [on]);

  const handleZoomOut = useCallback(() => {
    on.imagePreviewZoomOut?.();
  }, [on]);

  const handleForward = useCallback(() => {
    sessionStore?.setDraftMessage({ forwardId: message?.cli_msg_id });
    close();
  }, [message?.cli_msg_id, sessionStore, close]);

  const handleDownload = useCallback(() => {
    if (!fileMsg.file?.startsWith('blob:')) {
      tools.toaster.error({ title: t('tips.imageNotLoaded') });
      return;
    }
    downloadImage(fileMsg.file)
      .then(() => {
        tools.toaster.success({ title: t('tips.downloadSuccess') });
      })
      .catch(() => {
        tools.toaster.error({ title: t('tips.downloadFailed') });
      });
  }, [fileMsg, t]);

  const handleReply = useCallback(() => {
    sessionStore?.setDraftMessage({ replyId: message?.cli_msg_id });
    close();
  }, [message?.cli_msg_id, sessionStore, close]);

  const handleMessage = useCallback(async () => {
    if (!message) return;
    try {
      await sessionStore?.scrollToMessage(message, true);
      close();
    } catch (error) {
      console.error('Failed to scroll to message:', error);
    }
  }, [message, sessionStore, close]);

  const handleDelete = useCallback(async () => {
    if (!sessionStore || !message) return;

    try {
      await sessionStore.deleteMessages([message.cli_msg_id], deleteForEveryone);

      // tools.toaster.success({ title: t('tips.deleteSuccess') });
    } catch {
      tools.toaster.error({ title: t('tips.deleteFailed') });
    }
  }, [sessionStore, deleteForEveryone, message, t]);

  if (!slide || !message) return null;

  return (
    <Box position="absolute" top={0} left={0} right={0} pointerEvents="auto">
      <Flex
        justifyContent="space-between"
        alignItems="center"
        px="4"
        py="3"
        background="linear-gradient(0deg, rgba(0, 0, 0, 0) 10%, rgba(0, 0, 0, 1) 164.17%)"
        color="black"
      >
        <Flex gap="2.5" alignItems="center">
          <Avatar src={senderInfo?.avatar} name={senderInfo?.name} size="xs" activeShake={true} />
          <Box color="white">
            <Box>
              {senderInfo?.name && (
                <Text fontSize="sm" fontWeight="medium" lineHeight="normal">
                  {senderInfo.name}
                </Text>
              )}
            </Box>
            <Box>
              {message?.created_at && (
                <Text fontSize="xs" fontWeight="medium">
                  {dayjs(message.created_at).format('YYYY/M/D HH:mm')}
                </Text>
              )}
            </Box>
          </Box>
        </Flex>
        <Flex gap="2.5">
          <Box p="1" bg="rgba(0, 0, 0, 0.5)" borderRadius="34px" backdropFilter="blur(5px)">
            <Flex alignItems="center">
              <IconButton onClick={handleZoomOut} alt={t('buttons.zoomOut')}>
                <ZoomOutIcon color="white" />
              </IconButton>
              <IconButton onClick={handleZoomIn} alt={t('buttons.zoomIn')}>
                <ZoomInIcon color="white" />
              </IconButton>
              <Box mx="1" h="5" w="1px" backgroundColor="whiteAlpha.10"></Box>
              {canRestrictForward && (
                <IconButton onClick={handleForward} alt={t('buttons.forward')}>
                  <Forward2Icon color="white" />
                </IconButton>
              )}
              {canRestrictDownload && (
                <IconButton onClick={handleDownload} alt={t('buttons.download')}>
                  <Download2Icon color="white" />
                </IconButton>
              )}
              <IconButton reverse onClick={handleReply} alt={t('buttons.reply')}>
                <Forward2Icon color="white" />
              </IconButton>
              <IconButton reverse onClick={handleMessage} alt={t('buttons.jump')}>
                <MessageIcon color="white" />
              </IconButton>
              <IconButton
                onClick={() => deleteConfirmDialogRef.current?.open()}
                alt={t('buttons.delete')}
              >
                <Delete2Icon color="white" />
              </IconButton>
            </Flex>
          </Box>
          <Box p="1" bg="rgba(0, 0, 0, 0.5)" borderRadius="34px" backdropFilter="blur(5px)">
            <Flex gap={1}>
              <IconButton onClick={close} alt={t('buttons.close')}>
                <CloseIcon color="white" />
              </IconButton>
            </Flex>
          </Box>
        </Flex>
      </Flex>
      <DeleteConfirmDialog ref={deleteConfirmDialogRef} onConfirm={handleDelete}>
        {!canDeleteAll && ' '}
        {canDeleteAll && (
          <Checkbox.Root
            size="sm"
            defaultChecked={canDeleteAll}
            onCheckedChange={(e) => setDeleteForEveryone(!!e.checked)}
            cursor="pointer"
            px="5"
            colorPalette="brand"
          >
            <Checkbox.HiddenInput />
            <Checkbox.Control cursor="pointer" borderRadius="sm" />
            {isGroupChat ? (
              <Checkbox.Label>{t('chat.deleteMessagesForEveryone')}</Checkbox.Label>
            ) : (
              <Checkbox.Label>
                {t('chat.deleteMessagesForSession', { name: sessionStore?.session?.name })}
              </Checkbox.Label>
            )}
          </Checkbox.Root>
        )}
      </DeleteConfirmDialog>
    </Box>
  );
}

function SlideRender({ slide, ...props }: RenderSlideProps<SlideImage>) {
  const { currentSlide } = useLightboxState();
  const { imagePreview: { sessionStore } = {} } = useLightboxProps();

  const { src, message } = slide;

  const file = useMemo(
    () => (message?.cli_msg_id && sessionStore?.fileMsgs[message.cli_msg_id]?.file) || src,
    [sessionStore?.fileMsgs, message?.cli_msg_id, src]
  );

  const isCurrent = useMemo(
    () =>
      currentSlide?.type === 'image' && currentSlide?.message?.cli_msg_id === message?.cli_msg_id,
    [currentSlide?.message?.cli_msg_id, message?.cli_msg_id, currentSlide?.type]
  );

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const alt = message?.payload?.name || '';

  const refs = useRefs({
    isLoading,
    sessionStore,
    message,
  });

  useEffect(() => {
    const { isLoading, sessionStore, message } = refs.current;
    if (!isCurrent || isLoading || file?.startsWith('blob:')) return;
    setIsLoading(true);
    sessionStore?.getFile(message).finally(() => {
      setIsLoading(false);
    });
  }, [refs, isCurrent, file]);

  return (
    <>
      <ImageSlide slide={{ ...slide, alt, src: file }} {...props}></ImageSlide>
      {/* <Image
          src={src}
          alt={alt}
          draggable={false}
          onDragStart={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          objectFit="contain"
          maxW="100vw"
          maxH="100vh"
          css={{
            userSelect: 'none',
            pointerEvents: 'auto',
            objectPosition: 'center',
          }}
        /> */}
      {/* {isLoading && (
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          bg="rgba(0, 0, 0, 0.2)"
          pointerEvents="none"
        />
      )} */}
      {isLoading && (
        <Center position="absolute" w="100vw" h="100vh" pointerEvents="none">
          <Box bg="rgba(0, 0, 0, 0.2)" borderRadius="md" h="12" w="12" overflow="hidden" p="1">
            <Spinner
              h="10"
              w="10"
              color="brand.100"
              css={{ '--spinner-track-color': 'colors.whiteAlpha.50' }}
              borderWidth="0.25em"
              borderRadius="full"
            />
          </Box>
        </Center>
      )}
    </>
  );
}

export function ImagePreviewPlugin({ augment }: PluginProps) {
  augment(({ render, ...props }) => ({
    ...props,
    render: {
      ...render,
      slide: (props: RenderSlideProps<Slide>) =>
        props.slide.type !== 'image' ? null : (
          <SlideRender {...(props as RenderSlideProps<SlideImage>)} />
        ),
      controls: () => <Controllers />,
    },
  }));
}
